import { useParams, Link } from '@tanstack/react-router';
import { createFileRoute } from '@tanstack/react-router';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useJournal } from '@/hooks/use-journals';
import { format } from 'date-fns';
import { FileText, AlertCircle, User, ArrowLeft, Loader2 } from 'lucide-react';

export const Route = createFileRoute('/journals/$id')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <JournalDetailPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount);
}

function JournalDetailPage() {
  const { id } = useParams({ from: '/journals/$id' });
  const { data: journal, isLoading, error } = useJournal(id);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-muted-foreground">Loading journal entry...</p>
      </div>
    );
  }

  if (error || !journal) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <AlertCircle className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">Journal Entry Not Found</p>
        <p className="text-sm">The requested journal entry could not be loaded.</p>
        <Button asChild className="mt-4">
          <Link to="/journals">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Journals
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link to="/journals">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 truncate text-2xl font-bold tracking-tight sm:text-3xl">
              <FileText className="h-6 w-6" />
              {journal.journalNumber}
            </h1>
            <p className="text-muted-foreground">
              {(() => {
                try {
                  return format(new Date(journal.journalDate), 'dd MMMM yyyy');
                } catch {
                  return '-';
                }
              })()}
            </p>
          </div>
        </div>
        <Badge
          variant={journal.status === 'BALANCED' ? 'default' : 'destructive'}
          className="w-fit px-3 py-1"
        >
          {journal.status}
        </Badge>
      </div>

      {/* Details Card */}
      <Card>
        <CardHeader>
          <CardTitle>Journal Details</CardTitle>
          <CardDescription>General journal entry information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">Description</span>
              <p className="font-medium">{journal.description}</p>
            </div>
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">Reference Type</span>
              <div>
                <Badge variant="secondary">{journal.referenceType}</Badge>
              </div>
            </div>
            {journal.referenceNumber && (
              <div className="space-y-1">
                <span className="text-sm text-muted-foreground">Reference #</span>
                <p className="font-mono font-medium">{journal.referenceNumber}</p>
              </div>
            )}
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <User className="h-3 w-3" />
                Created By
              </span>
              <p className="font-medium">{journal.createdBy?.name || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Journal Lines Card */}
      <Card>
        <CardHeader>
          <CardTitle>Journal Lines</CardTitle>
          <CardDescription>Double-entry accounting records</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 sm:hidden">
            {(journal.lines || []).map((line) => {
              const type = line.type?.toString().toUpperCase();
              const valDebit = Number(line.debitAmount || line.debit || 0);
              const valCredit = Number(line.creditAmount || line.credit || 0);
              const valAmount = Number(line.amount) || 0;
              const debit = valDebit || (type === 'DEBIT' ? valAmount : 0);
              const credit = valCredit || (type === 'CREDIT' ? valAmount : 0);

              return (
                <article key={line.id} className="rounded-lg border bg-card p-3">
                  <p className="text-sm font-medium">
                    <span className="mr-2 font-mono text-muted-foreground">{line.account.code}</span>
                    {line.account.name}
                  </p>
                  {line.description ? (
                    <p className="mt-1 text-xs text-muted-foreground">{line.description}</p>
                  ) : null}
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div className="rounded-md bg-muted/40 px-2 py-1.5">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Debit</p>
                      <p className="font-semibold">{debit > 0 ? formatCurrency(debit) : '-'}</p>
                    </div>
                    <div className="rounded-md bg-muted/40 px-2 py-1.5">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Credit</p>
                      <p className="font-semibold">{credit > 0 ? formatCurrency(credit) : '-'}</p>
                    </div>
                  </div>
                </article>
              );
            })}
            <div className="rounded-lg border bg-muted/30 p-3 text-sm font-semibold">
              <div className="flex items-center justify-between">
                <span>Total Debit</span>
                <span>{formatCurrency(journal.totalDebit)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span>Total Credit</span>
                <span>{formatCurrency(journal.totalCredit)}</span>
              </div>
            </div>
          </div>

          <div className="hidden rounded-md border overflow-x-auto sm:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[50%]">Account</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(journal.lines || []).map((line) => {
                  const type = line.type?.toString().toUpperCase();
                  const valDebit = Number(line.debitAmount || line.debit || 0);
                  const valCredit = Number(line.creditAmount || line.credit || 0);
                  const valAmount = Number(line.amount) || 0;
                  const debit = valDebit || (type === 'DEBIT' ? valAmount : 0);
                  const credit = valCredit || (type === 'CREDIT' ? valAmount : 0);
                  const isCredit = credit > 0;

                  return (
                    <TableRow key={line.id}>
                      <TableCell className={isCredit ? "pl-8 md:pl-12" : ""}>
                        <div className="flex flex-col gap-1">
                          <div className="font-medium">
                            <span className="font-mono text-muted-foreground mr-2">
                              {line.account.code}
                            </span>
                            {line.account.name}
                          </div>
                          {line.description && (
                            <span className="text-xs text-muted-foreground">
                              {line.description}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {debit > 0 ? formatCurrency(debit) : ''}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {credit > 0 ? formatCurrency(credit) : ''}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter className="bg-muted/50 font-medium">
                <TableRow>
                  <TableCell className="text-right">Total</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(journal.totalDebit)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(journal.totalCredit)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Source Link */}
      {(journal as any).sourceId && journal.referenceType === 'ORDER' && (
        <Card>
          <CardHeader>
            <CardTitle>Source Document</CardTitle>
          </CardHeader>
          <CardContent>
            <Button variant="outline" asChild>
              <Link to={`/orders/${(journal as any).sourceId}` as any}>
                View Order {journal.referenceNumber}
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
